import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Input,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import PentagonIcon from "@mui/icons-material/Pentagon";
import CircleIcon from "@mui/icons-material/Circle";
import SquareIcon from "@mui/icons-material/Square";
import leaflet, { Control, Draw, Map } from "leaflet";
import { observer } from "mobx-react-lite";
import {
    GeoZoneType,
    getLeafletFromZone,
    getZoneFromLeaflet,
    Zone,
} from "../model";
import { useStore } from "../../../hooks/useStore";

interface UseZoneOptions {
    map: Map | undefined | null;
    defaultGeometry?: Zone | null;
    onCreated?: () => void;
    onNotCreated?: () => void;
}

const useZoneEdit = ({
    map,
    onCreated,
    onNotCreated,
    defaultGeometry,
}: UseZoneOptions) => {
    const [geometry] = useState<Zone | null>(defaultGeometry || null);
    const drawToolbar = useRef<leaflet.DrawToolbar>();
    const editToolbar = useRef<leaflet.EditToolbar>();
    const drawItems = useRef<leaflet.FeatureGroup>();

    useEffect(() => {
        if (!map) return;

        //@ts-ignore
        window.type = true;
        //@ts-ignore
        window.radius = true;

        drawItems.current = new leaflet.FeatureGroup();
        map.addLayer(drawItems.current);

        if (geometry) {
            const figure = getLeafletFromZone(geometry);
            drawItems.current.addLayer(figure);
        }

        const control = new Control.Draw({
            position: "topright",
            draw: {
                polyline: { metric: true },
                polygon: { metric: true },
                rectangle: { metric: true },
                circle: { metric: true },
            },
            edit: {
                featureGroup: drawItems.current,
                remove: true,
            },
        });
        map.addControl(control);

        let toolbar;
        ///@ts-ignore
        for (var toolbarId in control._toolbars) {
            ///@ts-ignore
            toolbar = control._toolbars[toolbarId];
            if (toolbar instanceof leaflet.DrawToolbar) {
                ///@ts-ignore
                drawToolbar.current = toolbar;
            }
            if (toolbar instanceof leaflet.EditToolbar) {
                ///@ts-ignore
                editToolbar.current = toolbar;
            }
        }

        map.on(Draw.Event.CREATED, (e) => {
            const layer = e.layer;

            drawItems.current!.addLayer(layer);

            if (onCreated) {
                console.log("Object created");
                setTimeout(onCreated);
            }
        });

        map.on(Draw.Event.DRAWSTOP, (e) => {
            const layer = e.layer;

            const objectWasNotCreated =
                drawItems.current!.getLayers().length < 1;

            if (objectWasNotCreated && onNotCreated) {
                console.log("Draw stopped, but object not created");
                setTimeout(onNotCreated, 1);
            }
        });

        return () => {
            map.off(Draw.Event.CREATED);
            map.off(Draw.Event.DRAWSTOP);
            if (drawItems.current) {
                drawItems.current.clearLayers();
                map.removeLayer(drawItems.current);
            }
            map.removeControl(control);
        };
    }, [map, defaultGeometry]);

    const isDrawing = () => {
        const toolbar = drawToolbar.current;

        //@ts-ignore
        if (toolbar && toolbar._activeMode) {
            //@ts-ignore
            return toolbar._activeMode.handler.enabled();
        }

        return false;
    };

    const startDrawing = (geozoneType: GeoZoneType) => {
        const toolbar = drawToolbar.current;
        if (!toolbar) return;

        switch (geozoneType) {
            case GeoZoneType.Circle:
                //@ts-ignore
                drawToolbar.current._modes.circle.handler.enable();
                break;
            case GeoZoneType.Square:
                //@ts-ignore
                drawToolbar.current._modes.rectangle.handler.enable();
                break;
            case GeoZoneType.Polygon:
                //@ts-ignore
                drawToolbar.current._modes.polygon.handler.enable();
                break;
            case GeoZoneType.Polyline:
                //@ts-ignore
                drawToolbar.current._modes.polyline.handler.enable();
                break;
        }
    };

    const stopDrawing = () => {
        const toolbar = drawToolbar.current;

        //@ts-ignore
        if (toolbar && toolbar._activeMode) {
            //@ts-ignore
            toolbar._activeMode.handler.disable();
        }
    };

    const isEditing = () => {
        const toolbar = editToolbar.current;

        if (toolbar) {
            //@ts-ignore
            return toolbar._modes.edit.handler.enabled() as boolean;
        }
        return false;
    };

    const startEditing = () => {
        const toolbar = editToolbar.current;

        if (toolbar) {
            //@ts-ignore
            toolbar._modes.edit.handler.enable();
        }
    };

    const stopEditing = () => {
        const toolbar = editToolbar.current;
        if (toolbar) {
            //@ts-ignore
            toolbar._modes.edit.handler.save();
            //@ts-ignore
            toolbar._modes.edit.handler.disable();
        }
    };

    const clearField = () => {
        if (!drawItems.current) return;

        drawItems.current.clearLayers();
    };

    const getJson = () => {
        const items = drawItems.current;
        if (!items) return null;

        const layers = items.getLayers();
        if (layers.length < 1) {
            return null;
        }

        const figureToSerialize = layers[0];

        return getZoneFromLeaflet(figureToSerialize);
    };

    const logHandlers = () => {
        console.dir(drawToolbar.current);
        console.dir(editToolbar.current);
    };

    return {
        isDrawing,
        startDrawing,
        stopDrawing,
        isEditing,
        startEditing,
        stopEditing,
        clearField,
        logHandlers,
        getJson,
    };
};

export const GeozoneEdit = observer(() => {
    const { geozones } = useStore();
    const { map, stopEdit, editingZoneRecord, saveEditGeoZone } = geozones;
    const editingZone = editingZoneRecord?.zone;
    const [name, setName] = useState(editingZoneRecord?.name || "");
    const [nameError, setNameError] = useState("");
    const [type, setType] = useState<GeoZoneType>(editingZone?.type || GeoZoneType.Circle);

    const handleNotCreated = () => {
        if (!isDrawing()) {
            startDrawing(type);
        }
    };

    const {
        isDrawing,
        startDrawing,
        stopDrawing,
        isEditing,
        startEditing,
        stopEditing,
        clearField,
        logHandlers,
        getJson,
    } = useZoneEdit({
        map: map,
        defaultGeometry: editingZone,
        onNotCreated: handleNotCreated,
        onCreated() {
            stopDrawing();
            startEditing();
        },
    });

    useEffect(() => {
        if (editingZone) {
            startEditing();
            setType(editingZone.type);
        } else {
            startDrawing(type);
        }
    }, [map]);

    const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    };

    const handleTypeChange = (_: any, value: GeoZoneType) => {
        stopDrawing();
        stopEditing();
        clearField();
        setType(value);
        startDrawing(value);
    };

    const handleSave = () => {
        stopDrawing();
        stopEditing();
        const zone = getJson();

        console.log(zone);

        if(zone === null){
            startDrawing(type);
            return
        }
        
        if(!validateName()){
            startEditing();
            return
        }

        saveEditGeoZone(name, zone);
    };

    const handleCancel = () => {
        stopEdit();
    };

    const validateName = () => {
        if(name.length < 1) {
            setNameError("Имя не может быть пустым");
            return false;
        }

        return true
    }

    return (
        <Stack padding={2} spacing={1} flexGrow={1}>
            <TextField
                error={!!nameError}
                helperText={nameError}
                label="Название геозоны"
                variant="outlined"
                value={name}
                onChange={handleNameChange}
                onFocus={() => setNameError("")}
            />
            <ToggleButtonGroup
                exclusive
                value={type}
                onChange={handleTypeChange}
            >
                <ToggleButton value={GeoZoneType.Circle}>
                    <CircleIcon />
                </ToggleButton>

                <ToggleButton value={GeoZoneType.Square}>
                    <SquareIcon />
                </ToggleButton>

                <ToggleButton value={GeoZoneType.Polygon}>
                    <PentagonIcon />
                </ToggleButton>
            </ToggleButtonGroup>

            <Stack
                direction="row"
                gap={2}
                alignSelf="flex-end"
                mt="auto !important"
            >
                <Button variant="outlined" onClick={handleCancel}>
                    Отмена
                </Button>
                <LoadingButton loading={geozones.isSaving} variant="contained" onClick={handleSave}>
                    Сохранить
                </LoadingButton>
            </Stack>
        </Stack>
    );
});
